import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { AdminAccessDrawer } from 'subapps/settings/components/drawers/NoAccess'
import Field from '@groovehq/internal-design-system/lib/components/Field/Field'
import ModalBtns from '@groovehq/internal-design-system/lib/components/ModalBtns/ModalBtns'
import Radio from '@groovehq/internal-design-system/lib/components/Radio/Radio'
import AnimatedEllipsis from '@groovehq/internal-design-system/lib/components/AnimatedEllipsis/AnimatedEllipsis'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { styles as fieldStyles } from '@groovehq/internal-design-system/lib/components/Field/Field.styles'
import {
  selectPendingRuleById,
  selectPendingRuleActionsByIds,
  selectPendingRuleConditionsByIds,
  selectIsLoadingRule,
  selectHasLoadedRule,
  selectHasErrorRule,
} from 'ducks/rules/selectors'
import { selectIsPaidAccount } from 'ducks/billing/selectors'
import {
  doUpdateSingleRule,
  doCreateSingleRule,
  doFetchRulesV1ById,
} from 'ducks/rules/actions'
import { v4 as uuidV4 } from 'uuid'
import { isValidEmail } from 'util/strings'
import { useConfirmHoldsCallback, useRhfDirtyHold } from 'util/dirtyHolds'
import { styles } from './styles'
import RuleConditions from './RuleConditions'
import RuleActions from './RuleActions'
import StateSwitch from './StateSwitch'

// eslint-disable-next-line func-names
yup.addMethod(yup.array, 'ruleConditions', function() {
  // eslint-disable-next-line func-names
  return this.test('ruleConditions', 'errorMessage', function(value) {
    const { path, createError } = this
    let isValid = false
    if (value) {
      const validFields = value.filter(
        field => !!field.operator && !!field.param
      )
      if (validFields.length > 0) isValid = true
    }

    return isValid || createError({ path, message: 'No conditions set' })
  })
})

// eslint-disable-next-line func-names
yup.addMethod(yup.object, 'ruleCondition', function() {
  // eslint-disable-next-line func-names
  return this.test('rule-condition', 'errorMessage', function(field) {
    if (!field) return true
    const { createError, options } = this
    const { index } = options
    const { operator, param, value } = field

    // we ignore empty conditions - these are handled by ruleConditions validation
    if (!operator || !param) return true

    if (param === 'to_email' && !isValidEmail(value)) {
      return createError({
        path: `conditions.${index}`,
        message: 'Invalid email',
      })
    }

    return true // isValid || createError({ path, message: 'Not valid' })
  })
})

const extractValidActions = fields => {
  return fields.filter(field => !!field.type)
}

// eslint-disable-next-line func-names
yup.addMethod(yup.array, 'ruleActions', function() {
  // eslint-disable-next-line func-names
  return this.test(`ruleActions`, 'errorMessage', function(value) {
    const { path, createError } = this
    let isValid = false
    if (value) {
      const validFields = extractValidActions(value)
      if (validFields.length > 0) isValid = true
    }

    return isValid || createError({ path, message: 'Not valid' })
  })
})

// keys should preferably match the object being stored in the entity store
// that way for updates, you can just spread the entity in reset() and not have to set each property individually
const FORM_SCHEMA = yup.object().shape({
  id: yup.string().nullable(),
  name: yup.string().required('Folder name is required'),
  active: yup.boolean(),
  conditions: yup
    .array()
    .ruleConditions()
    .of(yup.object().ruleCondition()),
  actions: yup.array().ruleActions(),
})

const MatchTypeSection = React.memo(({ register }) => {
  return (
    <>
      <div css={fieldStyles.labelBox} className="grui mt-16 mb-10">
        Run the rule when a conversation matches the following filters
      </div>
      <Radio
        id="allConditions"
        className="grui mb-8"
        value="all"
        {...register('match_type')}
      >
        Conversations that match ALL of the filters set below
      </Radio>
      <br />
      <Radio
        id="specificConditions"
        value="any"
        className="grui mb-5"
        {...register('match_type')}
      >
        Conversations that match ANY of the filters set below
      </Radio>
    </>
  )
})

const EMPTY_FORM_STATE = {
  // defaultValue has to match yup form schema
  id: null,
  name: null,
  state: 'ACTIVE',
  match_type: 'all',
  conditions: [
    {
      operator: null,
      param: null,
      value: '',
      key: uuidV4(),
    },
  ],
  actions: [
    {
      type: null,
      value: '',
      key: uuidV4(),
    },
  ],
}

export default function RuleCreateEditDrawer({
  open,
  onClose,
  onExit,
  drawerResourceId,
  drawerId,
  drawer,
  ...rest
}) {
  const dispatch = useDispatch()
  const [isSaving, setIsSaving] = useState(null)

  // no clue
  const isUpdate = drawerResourceId !== 'new'
  const [areFormFieldsLoaded, setAreFormFieldsLoaded] = useState(!isUpdate)

  useEffect(
    () => {
      // need to check drawer open state before fetch dispatches
      // otherwise the dispatch methods in useEffect below will run immediately
      // after this Drawer component is instantiated via `useDrawer` (before openDrawer is even called)
      // Which is unnecessary double dispatch triggering
      if (!open) return

      if (isUpdate) {
        dispatch(
          doFetchRulesV1ById(drawerResourceId, { targetStore: 'pending' })
        )
      }
    },
    [dispatch, isUpdate, drawerResourceId, open]
  )

  // selectors
  const isPaidAccount = useSelector(selectIsPaidAccount)

  const entity = useSelector(state =>
    selectPendingRuleById(state, drawerResourceId)
  )

  const ruleActionsEntities = useSelector(state =>
    selectPendingRuleActionsByIds(state, entity?.actions)
  )

  const ruleConditionsEntities = useSelector(state =>
    selectPendingRuleConditionsByIds(state, entity?.conditions)
  )

  const isLoadingRule = useSelector(selectIsLoadingRule)
  const hasLoadedRule = useSelector(selectHasLoadedRule)
  const hasErrorRule = useSelector(selectHasErrorRule)

  const notFound = isUpdate ? hasLoadedRule && !entity : false

  const resolver = yupResolver(FORM_SCHEMA, { abortEarly: false })
  // form
  const useFormMethods = useForm({
    mode: 'onBlur',
    resolver,
    defaultValues: EMPTY_FORM_STATE,
  })

  const {
    register,
    trigger,
    handleSubmit,
    reset,
    formState: { errors, isValid, isDirty },
    control,
    setValue,
  } = useFormMethods

  const { releaseHold, holdKey } = useRhfDirtyHold(drawerId, control)

  const handleClose = useConfirmHoldsCallback(
    holdKey,
    () => {
      reset(EMPTY_FORM_STATE)
      onClose()
    },
    [reset, onClose]
  )

  const handleExit = useConfirmHoldsCallback(
    null,
    () => {
      reset(EMPTY_FORM_STATE)
      onExit()
    },
    [reset, onExit]
  )

  const onSubmit = useCallback(
    async data => {
      setIsSaving(true)
      if (isUpdate) {
        await dispatch(doUpdateSingleRule(data))
      } else {
        await dispatch(doCreateSingleRule(data))
      }
      setIsSaving(false)
      releaseHold()
      handleClose()
    },
    [dispatch, handleClose, releaseHold, isUpdate]
  )

  // Effects
  useEffect(
    () => {
      if (isUpdate && entity) {
        reset({
          // just a spread here cos the needed keys should match the form schema
          ...entity,
          conditions: ruleConditionsEntities,
          actions: ruleActionsEntities,
        })
      }
    },
    // We intentionally exclude dependency props here to ensure that this
    // reset only triggers for the initial load
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entity, isUpdate, ruleConditionsEntities, ruleActionsEntities]
  )

  // Effects
  useEffect(
    () => {
      if (entity && isUpdate) {
        setAreFormFieldsLoaded(true)
      }
    },
    [entity, isUpdate]
  )

  // const formId = `${drawer}-${drawerId}-${drawerResourceId}`

  const footer = useMemo(
    () => {
      return (
        <ModalBtns
          saveBtnDisabled={!isValid || isSaving || !isDirty}
          saveBtnText={
            <>
              {isSaving && (
                <span>
                  Saving<AnimatedEllipsis />
                </span>
              )}
              {!isSaving && isUpdate && 'Save'}
              {!isSaving && !isUpdate && 'Create'}
            </>
          }
          // type="submit" will automatically trigger the form submit event when clicked
          saveBtnHtmlType="submit"
          //  saveBtnForm={formId}
          tertiaryBtnText="Cancel"
          onClickTertiaryBtn={handleClose}
        />
      )
    },
    [isValid, isSaving, isDirty, isUpdate, handleClose]
  )

  const DrawerForm = useCallback(
    subprops => <form onSubmit={handleSubmit(onSubmit)} {...subprops} />,
    [handleSubmit, onSubmit]
  )

  if (!drawer) return null

  const nameErrorsMessage = errors?.name?.message

  return (
    <AdminAccessDrawer
      {...rest}
      open={open}
      onClose={handleExit}
      size="wide"
      title={isUpdate ? 'Edit rule' : 'New rule'}
      isLoading={isLoadingRule || !areFormFieldsLoaded}
      isError={hasErrorRule}
      isNoResultFound={notFound}
      container={DrawerForm}
      footer={footer}
    >
      <FormProvider {...useFormMethods}>
        <div className="grui pt-10">
          <div css={styles.topFields} className="grui mb-12">
            <Field
              {...register('name')}
              label="Name"
              placeholder="Enter name..."
              name="name"
              validateStatus={nameErrorsMessage ? 'error' : undefined}
              help={nameErrorsMessage}
            />
            <div css={[fieldStyles.labelBox, styles.stateSwitch]}>
              <StateSwitch control={control} />
            </div>
          </div>
        </div>
        <div className="grui mb-12">
          <MatchTypeSection register={register} />
          <RuleConditions
            control={control}
            errors={errors.conditions}
            register={register}
            trigger={trigger}
            setValue={setValue}
          />
          <div css={fieldStyles.labelBox} className="grui mt-16 mb-10">
            Apply the following actions when the rule is run
          </div>
          <RuleActions
            control={control}
            setValue={setValue}
            trigger={trigger}
            isPaidAccount={isPaidAccount}
          />
        </div>
      </FormProvider>
    </AdminAccessDrawer>
  )
}
